import { useState, useEffect, useContext, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Card,
  CardHeader,
  Heading,
  CardBody,
  Container,
  Stack,
  StackDivider,
  Spinner,
  Text,
  Button,
} from '@chakra-ui/react'
import { AdSense } from 'components'
import styled from '@emotion/styled'
import { getSearch } from 'services/firebase'
import { userContext } from 'context/userContext'
import { DocumentData } from 'firebase/firestore'
import { SongTable } from 'components/general/SongTable'
import { format } from 'date-fns'

export const Search = () => {
  const [loading, setLoading] = useState<boolean>(true)
  const [search, setSearch] = useState<DocumentData | null>(null)
  const { id } = useParams()
  const { user } = useContext(userContext)
  const navigate = useNavigate()
  const userIsLoggedIn = Boolean(user.data)

  const stopLoader = () => {
    setTimeout(() => {
      setLoading(false)
    }, 1500)
  }

  useEffect(() => {
    if (id) {
      getSearch(id)
        .then((response) => {
          if (response && user?.data?.uid && response.userId === user.data.uid) {
            setSearch(response)
          }
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          stopLoader()
        })
    } else {
      stopLoader()
    }
  }, [userIsLoggedIn])

  const formattedDate = useMemo(() => {
    return search && isFinite(+new Date(search.createdAt))
      ? format(new Date(search.createdAt), 'MMMM do yyyy h:mma')
      : null
  }, [search])

  return (
    <Box w='100%' p={4} pb={8}>
      <Container maxW='3xl'>
        <AdWrapper>
          <AdSense dataAdSlot='7685568922' />
        </AdWrapper>
        {loading ? (
          <LoadingWrapper>
            <Spinner size='xl' />
          </LoadingWrapper>
        ) : search ? (
          <>
            <Card mb={8}>
              {formattedDate ? (
                <CardHeader>
                  <Heading size='md'>{formattedDate}</Heading>
                </CardHeader>
              ) : null}

              <CardBody>
                <Stack divider={<StackDivider />} spacing='4'>
                  <Box>
                    <Heading size='xs' textTransform='uppercase'>
                      Description
                    </Heading>
                    <Text pt='2' fontSize='sm'>
                      {search.description || '---'}
                    </Text>
                  </Box>
                  <Box>
                    <Heading size='xs' textTransform='uppercase'>
                      Moods
                    </Heading>
                    <Text pt='2' fontSize='sm'>
                      {search.moods && search.moods.length ? search.moods.join(', ') : '---'}
                    </Text>
                  </Box>
                  <Box>
                    <Heading size='xs' textTransform='uppercase'>
                      Theme
                    </Heading>
                    <Text pt='2' fontSize='sm'>
                      {search.theme || '---'}
                    </Text>
                  </Box>
                  <Box>
                    <Heading size='xs' textTransform='uppercase'>
                      Genre
                    </Heading>
                    <Text pt='2' fontSize='sm'>
                      {search.genre || '---'}
                    </Text>
                  </Box>
                </Stack>
              </CardBody>
            </Card>
            <SongTable songs={search.songs} />
            <ButtonWrapper>
              <Button colorScheme='gray' width='100%' onClick={() => navigate('/searches')}>
                Return to Searches
              </Button>
            </ButtonWrapper>
          </>
        ) : (
          <>
            <Text align='center'>No search found. Please try again</Text>
            <ButtonWrapper>
              <Button colorScheme='gray' width='100%' onClick={() => navigate('/')}>
                Return to Searches
              </Button>
            </ButtonWrapper>
          </>
        )}
      </Container>
    </Box>
  )
}

const AdWrapper = styled.div`
  margin: 2rem 0;
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 25rem;
`

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: 2rem;
`
